import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  CampaignOutlined,
  GraphicEq,
  LibraryMusic,
  LightbulbOutlined,
  LinearScale,
  LockOutlined,
  MusicNote,
  NavigateBefore,
  NavigateNext,
  PlayArrow,
  QueueMusic,
  TrendingUp,
  Videocam,
  WhatshotOutlined,
} from '@mui/icons-material';
import { getGlobalStats } from '../../core/progress';
import { getAllLearnSnapshots, type LearnModuleId } from '../../core/learn';
import {
  LEARN_FAMILIES,
  familyProgress,
  familyProgressLabel,
  getFamilyModules,
  getFeaturedModule,
  practiceHistory,
} from '../../core/modules/learnCatalog';
import type { LearnFamilyId, ModuleTemplate } from '../../core/types/module';
import { useEntitlement } from '../../core/entitlement';
import { WHATS_NEW } from '../../core/whatsNew';
import { useDisplayName } from '../../hooks/useDisplayName';
import { LearnPreview } from '../tools/_shared/LearnPreview';

const FAMILY_ICONS: Record<LearnFamilyId, React.ReactElement> = {
  pentatonic: <LinearScale />,
  blues: <LinearScale />,
  diatonic: <LinearScale />,
  triads: <MusicNote />,
  progressions: <QueueMusic />,
  soloing: <GraphicEq />,
  tabs: <LibraryMusic />,
};

const TIPS: string[] = [
  'Good light on the neck matters most — put a lamp in front of you, not behind.',
  'Keep the whole fretboard in frame; the overlay locks on faster when the headstock and body are visible.',
  'Check your camera in Settings before a focused Learn session.',
  'On a shape page, ← → change position and ↑ ↓ switch major/minor (or the string set for triads).',
  'Learn remembers your key and settings for each activity, so you can jump straight back in.',
  'Triads are easier to hear when you sing the root, third, and fifth before playing them.',
  'Start guided solo and chord progressions slowly, then nudge the tempo up once the changes feel easy.',
  'On scales and triads, press play and choose how many bars to spend on each shape. It loops back to the start at the end.',
  'Need to tune up? Turn on the tuner from the stage’s Tuner tab. It only uses your microphone while it’s shown.',
  'On a Mac, Continuity Camera lets you use your iPhone as a second angle on your fretboard.',
];

function FamilyCard({
  id,
  title,
  description,
  locked,
  progress,
}: {
  id: LearnFamilyId;
  title: string;
  description: string;
  locked: boolean;
  progress: { practiced: number; total: number };
}) {
  const progressLabel = familyProgressLabel(progress);
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        position: 'relative',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 28px rgba(3, 8, 21, 0.42)',
        },
      }}
    >
      {locked && (
        <Chip
          size="small"
          color="secondary"
          label="Pro"
          icon={<LockOutlined sx={{ fontSize: 14 }} />}
          sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}
        />
      )}
      <CardActionArea
        component={RouterLink}
        to={`/learn?family=${id}`}
        sx={{ height: '100%', p: 1.5 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              bgcolor: 'rgba(144, 202, 249, 0.12)',
              color: 'secondary.main',
            }}
          >
            {FAMILY_ICONS[id]}
          </Box>
          <Stack spacing={0.25} sx={{ minWidth: 0, pr: locked ? 5 : 0 }}>
            <Typography variant="body2" fontWeight={700}>
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.35 }}
            >
              {description}
            </Typography>
            {progressLabel && (
              <Stack spacing={0.5} sx={{ pt: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'success.light', fontWeight: 600 }}
                >
                  {progressLabel}
                </Typography>
                {progress.total > 1 && (
                  <LinearProgress
                    variant="determinate"
                    color="success"
                    value={(progress.practiced / progress.total) * 100}
                    aria-label={`${title}: ${progressLabel}`}
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

function StatRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 0.75 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="body2" fontWeight={700}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function Dashboard() {
  const displayName = useDisplayName();
  const ent = useEntitlement();
  const canUse = useCallback(
    (module: ModuleTemplate) =>
      !module.requiredEntitlement || ent.can(module.requiredEntitlement),
    [ent]
  );

  const [tipIndex, setTipIndex] = useState(() =>
    Math.floor(Math.random() * TIPS.length)
  );
  const nextTip = useCallback(
    () => setTipIndex((i) => (i + 1) % TIPS.length),
    []
  );
  const prevTip = useCallback(
    () => setTipIndex((i) => (i - 1 + TIPS.length) % TIPS.length),
    []
  );
  useEffect(() => {
    const id = setInterval(nextTip, 12_000);
    return () => clearInterval(id);
  }, [nextTip]);

  const globalStats = useMemo(() => getGlobalStats(), []);
  const snapshots = useMemo(() => getAllLearnSnapshots(), []);
  // Same "continue" logic as the Learn page, so both surfaces agree.
  const featured = getFeaturedModule(snapshots, canUse);
  const featuredSnapshot = snapshots[featured.id as LearnModuleId];
  const isReturning = Boolean(
    featuredSnapshot?.lastOpenedAt || featuredSnapshot?.lastPracticedAt
  );
  const families = useMemo(
    () =>
      LEARN_FAMILIES.map((family) => ({
        ...family,
        modules: getFamilyModules(family.id),
      })).filter((family) => family.modules.length > 0),
    []
  );
  const hasPracticed = globalStats.totalSessionsCompleted > 0;

  const rightRail = (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <TrendingUp fontSize="small" sx={{ color: 'success.main' }} />
            <Typography variant="overline" color="text.secondary">
              At a glance
            </Typography>
          </Stack>
          {hasPracticed ? (
            <>
              <StatRow
                icon={
                  <WhatshotOutlined
                    fontSize="small"
                    sx={{ color: 'warning.main' }}
                  />
                }
                label="Day streak"
                value={globalStats.dailyStreak}
              />
              <StatRow
                icon={
                  <TrendingUp fontSize="small" sx={{ color: 'success.main' }} />
                }
                label="Sessions logged"
                value={globalStats.totalSessionsCompleted}
              />
              <StatRow
                icon={
                  <AccessTime
                    fontSize="small"
                    sx={{ color: 'secondary.main' }}
                  />
                }
                label="Minutes played"
                value={Math.round(globalStats.totalPracticeTime / 60)}
              />
              <StatRow
                icon={<CalendarToday fontSize="small" color="primary" />}
                label="Last practiced"
                value={new Date(globalStats.lastSessionDate).toLocaleDateString(
                  undefined,
                  {
                    month: 'short',
                    day: 'numeric',
                  }
                )}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Finish your first run and your streak, sessions, and minutes will
              show up here.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <CampaignOutlined fontSize="small" color="secondary" />
            <Typography variant="overline" color="text.secondary">
              What&rsquo;s new
            </Typography>
          </Stack>
          <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.25 }}>
            {WHATS_NEW.map((item) => (
              <Typography
                component="li"
                key={item.href}
                variant="body2"
                color="text.secondary"
              >
                <Link
                  component={RouterLink}
                  to={item.href}
                  color="inherit"
                  underline="hover"
                >
                  {item.text}
                </Link>
              </Typography>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <LightbulbOutlined
              fontSize="small"
              sx={{ color: 'warning.main' }}
            />
            <Typography variant="overline" color="text.secondary">
              Tip {tipIndex + 1} / {TIPS.length}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ minHeight: 56 }} aria-live="polite">
            {TIPS[tipIndex]}
          </Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
            <IconButton
              size="small"
              onClick={prevTip}
              aria-label="Previous tip"
            >
              <NavigateBefore fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={nextTip} aria-label="Next tip">
              <NavigateNext fontSize="small" />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );

  const mainColumn = (
    <Stack spacing={3}>
      <PageHeader
        title={`${isReturning ? 'Welcome back' : 'Welcome'}${
          displayName ? `, ${displayName}` : ''
        }`}
        subtitle={
          isReturning
            ? 'Pick up where you left off, or try something new.'
            : 'Choose something to play and see it on your guitar.'
        }
      />

      <Card
        elevation={0}
        sx={{ backgroundImage: (theme) => theme.palette.gradient.soft }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'minmax(0, 1fr) minmax(200px, 0.6fr)',
              },
              gap: { xs: 2, sm: 3 },
              alignItems: 'center',
            }}
          >
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="overline" color="text.secondary">
                {isReturning
                  ? 'Pick up where you left off'
                  : 'A good place to start'}
              </Typography>
              <Typography variant="h6" component="h2">
                {featuredSnapshot?.lastTitle ?? featured.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {practiceHistory(featuredSnapshot) ??
                  (featured.estimatedMinutes
                    ? `About ${featured.estimatedMinutes} min`
                    : featured.valueProposition)}
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrow />}
                component={RouterLink}
                to={featured.configRoute}
                sx={{ mt: 1, whiteSpace: 'nowrap' }}
              >
                {isReturning ? 'Resume' : 'Start practice'}
              </Button>
            </Stack>
            <Box sx={{ maxWidth: 320, width: '100%', justifySelf: 'center' }}>
              <LearnPreview module={featured} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Stack spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" component="h2" fontWeight={700}>
            Explore
          </Typography>
          <Button component={RouterLink} to="/learn" size="small">
            All activities
          </Button>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}
        >
          {families.map((family) => (
            <FamilyCard
              key={family.id}
              id={family.id}
              title={family.title}
              description={family.outcome}
              locked={!family.modules.some(canUse)}
              progress={familyProgress(family.id, snapshots)}
            />
          ))}
        </Box>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" component="h2" fontWeight={700}>
                Free play on the stage
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Open the camera with your scale overlay and just play &mdash; no
                exercise, no scoring.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Videocam />}
              component={RouterLink}
              to="/cam"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Open stage
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
        Need help, found a bug, or want a feature?{' '}
        <Link href="mailto:support@jamcam.io" color="secondary.main">
          Email support@jamcam.io
        </Link>
      </Typography>
    </Stack>
  );

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 8 }}>{mainColumn}</Grid>
      <Grid size={{ xs: 12, md: 4 }}>{rightRail}</Grid>
    </Grid>
  );
}
